import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EtatReservationCreneau, TypeRdvEntree } from '@artosera/shared';
import { api, requeteApi, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { useMutation } from './mutation.js';

export interface TypeRdv extends TypeRdvEntree {
  id: string;
  archiveLe: string | null;
  version: number;
}
export interface ReglagesRdv {
  fuseau: string;
  simultanesMax: number;
  annulationVisiteurHeures: number;
  messageConfirmation: { fr: string; en: string };
  surLeSite: boolean;
  notifierMembres: boolean;
  version: number;
}
export interface ExceptionRdv {
  id: string;
  sorte: 'fermeture' | 'ouverture';
  du: string;
  au: string;
  typeIds: string[];
  note: string;
  version: number;
}
export interface ReservationRdv {
  id: string;
  numero: string;
  typeId: string;
  typeTitre: { fr: string; en: string };
  debut: string;
  fin: string;
  membreId: string;
  membreNom: string;
  etat: EtatReservationCreneau;
  visiteur: { nom: string; email: string; telephone: string; langue: 'fr' | 'en'; message: string };
  oeuvreId: string | null;
  oeuvreTitre: string | null;
  contactId: string | null;
  rendezVousId: string | null;
  consentement: { version: string; accordeLe: string };
  newsletter: boolean;
  historique: {
    de: EtatReservationCreneau | null;
    vers: EtatReservationCreneau;
    le: string;
    par: string | null;
    motif: string;
  }[];
  message: string;
  emailConfirmeLe: string | null;
  decideeLe: string | null;
  decideePar: string | null;
  annuleeLe: string | null;
  annuleePar: 'visiteur' | 'galerie' | null;
  contactEfface: boolean;
  version: number;
  agendaModifie: boolean;
}
export type VueRdv = 'a_valider' | 'a_venir' | 'passees' | 'closes';
export type GesteRdv = 'valider' | 'refuser' | 'annuler' | 'deplacer';
export interface CreneauRdvPresente {
  debut: string;
  fin: string;
  membreId: string;
}
export interface ListeReservationsRdv {
  reservations: ReservationRdv[];
  aValider: number;
  pagination: { page: number; parPage: number; total: number };
  lienPublic: string;
}
const base = (g: string) => `/g/${encodeURIComponent(g)}/prise-rendez-vous`;
const idRoute = (id: string) => encodeURIComponent(id);
const query = (valeurs: object) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(valeurs))
    if (v !== undefined && v !== '') p.set(k, String(v));
  return p.toString();
};
export const priseRdvApi = {
  reservations: (
    g: string,
    filtres: { vue: VueRdv; typeId?: string; membreId?: string; page: number },
    appel?: AppelApi,
  ) =>
    api.get<ListeReservationsRdv>(`${base(g)}/reservations?${query(filtres)}`, {
      galerie: g,
      ...appel,
    }),
  reservation: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ reservation: ReservationRdv }>(`${base(g)}/reservations/${idRoute(id)}`, {
      galerie: g,
      ...appel,
    }),
  geste: (
    g: string,
    id: string,
    geste: GesteRdv,
    corps: { version: number; message?: string; debut?: string; force?: boolean },
    appel?: AppelApi,
  ) =>
    api.post<{ reservation: ReservationRdv }>(
      `${base(g)}/reservations/${idRoute(id)}/${geste}`,
      corps,
      { galerie: g, ...appel },
    ),
  types: (g: string, appel?: AppelApi) =>
    api.get<{ types: TypeRdv[] }>(`${base(g)}/types`, { galerie: g, ...appel }),
  creerType: (g: string, corps: TypeRdvEntree, appel?: AppelApi) =>
    api.post<{ type: TypeRdv }>(`${base(g)}/types`, corps, { galerie: g, ...appel }),
  modifierType: (
    g: string,
    id: string,
    corps: Partial<TypeRdvEntree> & { version: number },
    appel?: AppelApi,
  ) =>
    api.patch<{ type: TypeRdv }>(`${base(g)}/types/${idRoute(id)}`, corps, {
      galerie: g,
      ...appel,
    }),
  archiverType: (g: string, id: string, version: number, appel?: AppelApi) =>
    api.post<{ type: TypeRdv }>(
      `${base(g)}/types/${idRoute(id)}/archiver`,
      { version },
      { galerie: g, ...appel },
    ),
  creneaux: (g: string, id: string, du: string, au: string, appel?: AppelApi, ignorer?: string) =>
    api.get<{ creneaux: CreneauRdvPresente[] }>(
      `${base(g)}/types/${idRoute(id)}/creneaux?${query(ignorer ? { du, au, ignorer } : { du, au })}`,
      { galerie: g, ...appel },
    ),
  exceptions: (g: string, appel?: AppelApi) =>
    api.get<{ exceptions: ExceptionRdv[] }>(`${base(g)}/exceptions`, { galerie: g, ...appel }),
  creerException: (g: string, corps: Omit<ExceptionRdv, 'id' | 'version'>, appel?: AppelApi) =>
    api.post<{ exception: ExceptionRdv }>(`${base(g)}/exceptions`, corps, { galerie: g, ...appel }),
  modifierException: (g: string, id: string, corps: Omit<ExceptionRdv, 'id'>, appel?: AppelApi) =>
    api.patch<{ exception: ExceptionRdv }>(`${base(g)}/exceptions/${idRoute(id)}`, corps, {
      galerie: g,
      ...appel,
    }),
  supprimerException: (g: string, id: string, version: number, appel?: AppelApi) =>
    requeteApi<void>(`${base(g)}/exceptions/${idRoute(id)}`, {
      method: 'DELETE',
      galerie: g,
      corps: { version },
      ...appel,
    }),
  reglages: (g: string, appel?: AppelApi) =>
    api.get<{ reglages: ReglagesRdv }>(`${base(g)}/reglages`, { galerie: g, ...appel }),
  regler: (g: string, corps: ReglagesRdv, appel?: AppelApi) =>
    api.put<{ reglages: ReglagesRdv }>(`${base(g)}/reglages`, corps, { galerie: g, ...appel }),
};
export function useTypesRdv(g: string, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'types'),
    queryFn: (c) => priseRdvApi.types(g, appelDeRequete(c)),
    enabled,
  });
}
export function useReglagesRdv(g: string, enabled = true) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'reglages'),
    queryFn: (c) => priseRdvApi.reglages(g, appelDeRequete(c)),
    enabled,
  });
}
export function useReservationsRdv(
  g: string,
  filtres: Parameters<typeof priseRdvApi.reservations>[1],
  enabled = true,
) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'reservations', filtres),
    queryFn: (c) => priseRdvApi.reservations(g, filtres, appelDeRequete(c)),
    enabled,
  });
}
export function useReservationRdv(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'reservation', id),
    queryFn: (c) => priseRdvApi.reservation(g, id, appelDeRequete(c)),
  });
}
export function useExceptionsRdv(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'exceptions'),
    queryFn: (c) => priseRdvApi.exceptions(g, appelDeRequete(c)),
  });
}
export function useCreneauxRdv(g: string, id: string, du: string, au: string, ignorer?: string) {
  return useQuery({
    queryKey: cleMetier(g, 'prise-rendez-vous', 'creneaux', id, du, au, ignorer ?? ''),
    queryFn: (c) => priseRdvApi.creneaux(g, id, du, au, appelDeRequete(c), ignorer),
    enabled: !!id && !!du && !!au,
  });
}
export function useActionRdv<T, V>(g: string, action: (v: V, appel: AppelApi) => Promise<T>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: action,
      onSettled: async () => {
        await Promise.all(
          ['prise-rendez-vous', 'agenda'].map((d) =>
            client.invalidateQueries({ queryKey: cleMetier(g, d) }),
          ),
        );
      },
    }),
  );
}
